import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LearningProgram } from '../../types/program';
import { useImageUrl } from '../../services/uploads';
import { ClockIcon, TrashIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import ProgramStatusBadge from './ProgramStatusBadge';
import ActionMenu, { ActionMenuItem } from '../common/ActionMenu';
import SettingsIcon from '../icons/SettingsIcon';
import PencilIcon from '../icons/PencilIcon';
import HiddenIcon from '../icons/HiddenIcon';

interface ProgramCardProps {
  program: LearningProgram;
  onToggleHidden?: (program: LearningProgram) => void;
  onDelete?: (program: LearningProgram) => void;
  'data-testid'?: string;
}

const ProgramCard: React.FC<ProgramCardProps> = ({
  program,
  onToggleHidden,
  onDelete,
  'data-testid': testId = 'program-card',
}) => {
  const navigate = useNavigate();
  const [resolvedImageUrl] = useImageUrl(program.header_image);

  // Same settings menu as the course card (and the program detail page).
  const hidden = program.status === 'archived' || program.status === 'disabled';
  const menuItems: ActionMenuItem[] = [
    {
      label: 'Edit',
      icon: <PencilIcon className="w-4 h-4" color="currentColor" accentColor={'rgb(var(--color-primary-100))'} />,
      onClick: () => navigate(`/programs/${program.id}/edit`),
      'data-testid': `${testId}-menu-edit`,
    },
    {
      label: 'Configure',
      icon: <SettingsIcon className="w-4 h-4" color="currentColor" accentColor={'rgb(var(--color-primary-100))'} />,
      onClick: () => navigate(`/programs/${program.id}/configure`),
      'data-testid': `${testId}-menu-configure`,
    },
    {
      label: hidden ? 'Unhide' : 'Hide',
      icon: <HiddenIcon className="w-4 h-4" color="currentColor" accentColor={'rgb(var(--color-primary-100))'} />,
      onClick: () => onToggleHidden?.(program),
      'data-testid': `${testId}-menu-hide`,
    },
    {
      label: 'Delete',
      icon: <TrashIcon className="w-4 h-4" />,
      onClick: () => onDelete?.(program),
      isDestructive: true,
      separatorAbove: true,
      'data-testid': `${testId}-menu-delete`,
    },
  ];

  const formatDate = (dateStr: string): string => {
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
  };

  const formatDuration = (days: number): string => {
    if (days === 1) return '1 day';
    return `${days} days`;
  };

  return (
    <div
      data-testid={testId}
      onClick={() => navigate(`/programs/${program.id}`)}
      className="relative flex flex-col w-full rounded-[28px] overflow-hidden border-[7px] border-[rgba(189,188,201,0.24)] bg-surface-primary shadow-[7px_7px_14px_0px_rgba(0,0,0,0.2)] group cursor-pointer"
    >
      {/* Cover banner — short image strip, badges overlaid */}
      <div className="relative h-[150px] shrink-0 overflow-hidden">
        {resolvedImageUrl ? (
          <img
            src={resolvedImageUrl}
            alt={program.name}
            className="absolute inset-0 w-full h-full object-cover"
            data-testid={`${testId}-image`}
          />
        ) : (
          <div
            className="absolute inset-0 bg-gradient-to-br from-primary-400 to-primary-700"
            data-testid={`${testId}-placeholder`}
          />
        )}

        <div className="absolute inset-x-0 top-0 flex items-center justify-between p-3">
          <div className="relative flex items-center gap-1.5 px-1.5 py-0.5 rounded-[6px] overflow-hidden">
            <div className="absolute inset-0 bg-white/75 rounded-[6px]" />
            <ClockIcon className="relative w-4 h-4 text-grey-900" />
            <span className="relative text-xs font-semibold text-grey-900">
              {formatDuration(program.duration_days)}
            </span>
          </div>

          <div className="relative flex items-center gap-1 px-2 py-0.5 rounded-[6px] overflow-hidden">
            <div className="absolute inset-0 bg-black/50 rounded-[6px]" />
            <UserGroupIcon className="relative w-4 h-4 text-white" />
            <span className="relative text-[13px] font-extrabold text-white">
              {program.audiences_count} {program.audiences_count === 1 ? 'Audience' : 'Audiences'}
            </span>
          </div>
        </div>
      </div>

      {/* Footer — solid, theme-aware */}
      <div className="flex flex-col gap-2 p-4">
        <h3
          className="text-base font-extrabold text-text-primary leading-tight line-clamp-2 min-h-[2.5em]"
          data-testid={`${testId}-name`}
        >
          {program.name}
        </h3>

        <div className="flex items-center gap-2">
          <ProgramStatusBadge status={program.status} />
          <span className="text-[13px] text-text-tertiary">Updated {formatDate(program.updated_at)}</span>
        </div>

        <div className="flex items-center gap-2 mt-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/programs/${program.id}`);
            }}
            className="flex-1 py-1.5 px-2 text-[13px] font-bold bg-primary-500 rounded-full hover:bg-primary-600 dark:hover:bg-primary-400 transition-[background-color] duration-150"
            data-testid={`${testId}-view-btn`}
          >
            View Program
          </button>

          <div onClick={(e) => e.stopPropagation()}>
            <ActionMenu
              items={menuItems}
              placement="top"
              align="right"
              data-testid={`${testId}-menu`}
            >
              {({ open, toggle }) => (
                <button
                  type="button"
                  onClick={toggle}
                  aria-haspopup="menu"
                  aria-expanded={open}
                  aria-label="Program actions"
                  className="flex items-center justify-center w-8 h-8 shrink-0 bg-neutral-100 dark:bg-neutral-800 border border-border-primary/20 rounded-full hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-[background-color] duration-150"
                  data-testid={`${testId}-menu-btn`}
                >
                  <SettingsIcon className="w-4 h-4 text-text-secondary" accentColor="transparent" />
                </button>
              )}
            </ActionMenu>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramCard;
